import { JsonPipe, NgTemplateOutlet } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, input, signal } from "@angular/core";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faMinus, faPlay } from "@fortawesome/free-solid-svg-icons";
import { UserRoles } from "src/app/core/models/user.model";
import { HEALTH_SCORE_WEIGHTS, SchoolHealthScore } from "src/app/core/services/partner-success.service";
import { SortColumn, sortUpdate } from "src/app/core/utils/table";
import { ColorCategoryComponent } from "src/app/shared/components/color-category/color-category.component";
import { AbsValuePipe } from "src/app/shared/pipes/abs-value.pipe";

@Component({
  selector: 'wf-partner-success-health',
  imports: [
    NgTemplateOutlet,
    ColorCategoryComponent,
    AbsValuePipe,
    FontAwesomeModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: 'partner-success-health.component.css',
  template: `
    <table class="report-table report-table-white">
      <colgroup>
        <col style="width: 15%;"/>
        <col style="width: 15%;"/>
        <col style="width: 12%;"/>
        <col/>
        <col/>
        <col/>
        <col/>
        <col/>
        <col/>
        @if (userRole() === UserRoles.FOUNDATIONS_REPORTS) {
          <col/>
        }
      </colgroup>
      <thead>
        <ng-template #sortableTableHeader let-column="column">
          <th class="sortable-report-table-header">
            <button
              (click)="updateSort(column)"
              [attr.aria-sort]="sortColumn().column === column ? (sortColumn().reverse ? 'desending' : 'ascending') : undefined"
            >
              <div>
                {{ column }}
                @if (column === COLUMNS.OVERALL_HEALTH) {
                  <br/>
                  <span class="table-header-subtext">and 30 Day Change</span>
                }
              </div>
              <div
                [class]=" { 'arrow-up' : sortColumn().reverse, 'arrow-down': !sortColumn().reverse }"
                [style.visibility]="(sortColumn().column !== column) ? 'hidden' : 'visible'"
                class="print-hide"
              ></div>
            </button>
          </th>
        </ng-template>
        <tr>
          <ng-container [ngTemplateOutlet]="sortableTableHeader" [ngTemplateOutletContext]="{ column: COLUMNS.DISTRICT }"/>
          <ng-container [ngTemplateOutlet]="sortableTableHeader" [ngTemplateOutletContext]="{ column: COLUMNS.SCHOOL }"/>
          <ng-container [ngTemplateOutlet]="sortableTableHeader" [ngTemplateOutletContext]="{ column: COLUMNS.OVERALL_HEALTH }"/>
          <ng-container [ngTemplateOutlet]="sortableTableHeader" [ngTemplateOutletContext]="{ column: COLUMNS.SCHOOL_LOGIN }"/>
          <ng-container [ngTemplateOutlet]="sortableTableHeader" [ngTemplateOutletContext]="{ column: COLUMNS.TEACHER_LOGIN }"/>
          <ng-container [ngTemplateOutlet]="sortableTableHeader" [ngTemplateOutletContext]="{ column: COLUMNS.STUDENT_USAGE }"/>
          <ng-container [ngTemplateOutlet]="sortableTableHeader" [ngTemplateOutletContext]="{ column: COLUMNS.PROFESSIONAL_LEARNING_ATTENDANCE }"/>
          <ng-container [ngTemplateOutlet]="sortableTableHeader" [ngTemplateOutletContext]="{ column: COLUMNS.TEACHERS }"/>
          <ng-container [ngTemplateOutlet]="sortableTableHeader" [ngTemplateOutletContext]="{ column: COLUMNS.STUDENTS }"/>
          @if (userRole() === UserRoles.FOUNDATIONS_REPORTS) {
            <ng-container [ngTemplateOutlet]="sortableTableHeader" [ngTemplateOutletContext]="{ column: COLUMNS.PARTNER_SUCCESS_USER }"/>
          }
        </tr>
      </thead>
      <tbody>
        @for (score of scoresSorted(); track score.school.schoolID) {
          <tr>
            <td>{{ score.district.name }}</td>
            <th scope="row">{{ score.school.name }}</th>
            <td>
              <div class="overall-health-container">
                @let level = score.overallHealth >= 80 ? 'green' : (score.overallHealth < 60 ? 'red' : 'yellow');
                <wf-color-category [level]="level">
                  {{ score.overallHealth }}
                </wf-color-category>
                @if(score.overallHealthChangeFromPrevMonth !== undefined) {
                  <div>
                    @if(score.overallHealthChangeFromPrevMonth === 0) {
                      <fa-icon
                        [icon]="noChangeIcon"
                        aria-label="no change in score from 30 days ago"
                      />
                    } @else {
                      @let absScore =  score.overallHealthChangeFromPrevMonth | abs;
                      {{ absScore }}
                      @let direction = score.overallHealthChangeFromPrevMonth > 0 ? 'increased' : 'decreased';
                      <fa-icon
                        [attr.aria-label]="'score has ' + direction + ' ' + absScore + ' from 30 days ago'"
                        class="score-{{ direction }}"
                        [icon]="changeIcon"
                        [rotate]="score.overallHealthChangeFromPrevMonth > 0 ? 270 : 90"
                      />
                    }
                  </div>
                }
              </div>
            </td>
            <td> {{ score.resourceHealth }} </td>
            <td> {{ score.loginHealth }} </td>
            <td> {{ score.studentUsageHealth }} </td>
            <td> {{ score.professionalLearningHealth }} </td>
            <td> {{ score.teacherCount }} </td>
            <td> {{ score.studentCount }} </td>
            @if (userRole() === UserRoles.FOUNDATIONS_REPORTS) {
              <td>
                @let partnerSuccessContact = score.school.partnerSuccessContact;
                @if (partnerSuccessContact) {
                  {{ partnerSuccessContact.firstName }} {{ partnerSuccessContact.lastName[0] }}.
                } @else {
                  N/A
                }
              </td>
            }
          </tr>
        }
      </tbody>
    </table>
  `,
})
export class PartnerSuccessHealthComponent {
  readonly scores = input.required<SchoolHealthScore[]>();
  readonly userRole = input.required<string | undefined>();

  readonly UserRoles = UserRoles;

  readonly noChangeIcon = faMinus;
  readonly changeIcon = faPlay;

  readonly COLUMNS = {
    DISTRICT: 'District',
    SCHOOL: 'School',
    OVERALL_HEALTH: 'Overall Health',
    SCHOOL_LOGIN: `School User Login (${HEALTH_SCORE_WEIGHTS.SCHOOL_USER_LOGIN})`,
    TEACHER_LOGIN: `Teacher Logins (${HEALTH_SCORE_WEIGHTS.TEACHER_LOGIN})`,
    STUDENT_USAGE: `Student Usage (${HEALTH_SCORE_WEIGHTS.STUDENT_USAGE})`,
    PROFESSIONAL_LEARNING_ATTENDANCE: `PL Attendance (${HEALTH_SCORE_WEIGHTS.PROFESSIONAL_LEARNING_ATTENDANCE})`,
    TEACHERS: '# Teachers',
    STUDENTS: '# Students',
    PARTNER_SUCCESS_USER: 'Partner Success',
  } as const;

  readonly sortColumn = signal<SortColumn>({
    column: this.COLUMNS.DISTRICT,
    reverse: false,
  });

  readonly scoresSorted = computed(() => {
    const { column, reverse } = this.sortColumn();

    let cmp = (s1: SchoolHealthScore, s2: SchoolHealthScore) => {
      const districtThenSchool = s1.district.name.localeCompare(s2.district.name) || s2.school.name.localeCompare(s2.school.name);

      let comp = districtThenSchool;
      if (column === this.COLUMNS.SCHOOL) {
        comp = s1.school.name.localeCompare(s2.school.name);
      } else if (column === this.COLUMNS.OVERALL_HEALTH) {
        comp = s1.overallHealth - s2.overallHealth;
      } else if (column === this.COLUMNS.SCHOOL_LOGIN) {
        comp = s1.resourceHealth - s2.resourceHealth;
      } else if (column === this.COLUMNS.TEACHER_LOGIN) {
        comp = s1.loginHealth - s2.loginHealth;
      } else if (column === this.COLUMNS.STUDENT_USAGE) {
        comp = s1.studentUsageHealth - s2.studentUsageHealth;
      } else if (column === this.COLUMNS.PROFESSIONAL_LEARNING_ATTENDANCE) {
        comp = s1.professionalLearningHealth - s2.professionalLearningHealth;
      } else if (column === this.COLUMNS.TEACHERS) {
        comp = s1.teacherCount - s2.teacherCount;
      } else if (column === this.COLUMNS.STUDENTS) {
        comp = s1.studentCount - s2.studentCount;
      } else if (column === this.COLUMNS.PARTNER_SUCCESS_USER) {
        const last = "Z".repeat(10);
        comp = (s1.school.partnerSuccessContact?.firstName ?? last).localeCompare(s2.school.partnerSuccessContact?.firstName ?? last) ||
          (s1.school.partnerSuccessContact?.lastName ?? last).localeCompare(s2.school.partnerSuccessContact?.lastName ?? last);
      }

      if (comp === 0 && column !== this.COLUMNS.DISTRICT) {
        // On tie of non-district columns, fall back to ascending name
        // This ignores reverse. Reverse should only affect the top level sort, not the tiebreaker.
        return districtThenSchool
      }

      return reverse ? (comp * -1) : comp
    }

    return [...this.scores()].sort(cmp);
  });

  updateSort(clickedColumn: string) {
    this.sortColumn.update(sortUpdate(clickedColumn));
  }
}
